<script setup lang="ts">
import type { PanelTab } from '../../../lib/brandAppSections'
import PanelGroup from '../../PanelGroup.vue'
import { BRAND_APP_SOCIALS, type BrandAppSocialId } from '../../../lib/brandAppContract'
import useBrandAppConfig from '../../../composables/useBrandAppConfig'
import { isInvalidUrlInput } from '../../../lib/brandAppUrl'
import {
  SOCIAL_HANDLE_LABEL,
  handleFromUrl,
  isHandleSocial,
  isInvalidHandle,
  urlFromHandle
} from '../../../lib/brandAppSocialHandles'
import BlockTitleField from './BlockTitleField.vue'

defineProps<{ tab?: PanelTab }>()

const { t } = useI18n()
const { socials, updateSocials } = useBrandAppConfig()

const WEBSITE_PREFIX = 'https://'

const withoutProtocol = (value: string) => value.trim().replace(/^https?:\/\//i, '')

const prefixLabel = (id: BrandAppSocialId) => (isHandleSocial(id) ? SOCIAL_HANDLE_LABEL[id] : WEBSITE_PREFIX)

const inputValue = (id: BrandAppSocialId) =>
  isHandleSocial(id) ? handleFromUrl(id, socials.value[id]) : withoutProtocol(socials.value[id] ?? '')

const setLink = (id: BrandAppSocialId, input: string) => {
  if (isHandleSocial(id)) {
    updateSocials(id, urlFromHandle(id, input))
    return
  }

  const address = withoutProtocol(input)
  updateSocials(id, address ? `${WEBSITE_PREFIX}${address}` : '')
}

const fieldErrors = (id: BrandAppSocialId) => {
  if (isHandleSocial(id)) {
    return isInvalidHandle(id, socials.value[id])
      ? [t('pages.loyaltySupports.webapp.editor.validation.invalidHandle')]
      : []
  }

  return isInvalidUrlInput(socials.value[id]) ? [t('pages.loyaltySupports.webapp.editor.validation.invalidUrl')] : []
}
</script>

<template>
  <PanelGroup v-if="tab !== 'content'">
    <BlockTitleField block-id="social" />
  </PanelGroup>

  <PanelGroup v-if="tab !== 'text'" :title="t('pages.loyaltySupports.webapp.editor.social.title')">
    <div class="flex flex-col gap-5">
      <div v-for="social in BRAND_APP_SOCIALS" :key="social.id" class="flex flex-col gap-1.5">
        <Label :for="`brandApp.socials.${social.id}`">{{ social.label }}</Label>
        <div class="flex items-stretch">
          <span
            class="border-input bg-muted text-muted-foreground flex items-center rounded-l-md border border-r-0 px-3 text-sm whitespace-nowrap"
          >
            {{ prefixLabel(social.id) }}
          </span>
          <Input
            :id="`brandApp.socials.${social.id}`"
            class="min-w-0 flex-1 rounded-l-none"
            :placeholder="social.placeholder"
            :model-value="inputValue(social.id)"
            @update:model-value="setLink(social.id, String($event))"
          />
        </div>
        <p v-for="error in fieldErrors(social.id)" :key="error" class="text-destructive text-sm">{{ error }}</p>
      </div>
    </div>
  </PanelGroup>
</template>
